// frontend/src/pages/TemplateEditorPage.tsx
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import { templateApi } from '../lib/templateApi';
import type { TemplateRow, ValidationResult } from '../lib/templateApi';

export default function TemplateEditorPage() {
  const [selected, setSelected] = useState<TemplateRow | null>(null);
  const [content, setContent] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [testMessageName, setTestMessageName] = useState('');
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { data: templates = [], refetch } = useQuery({
    queryKey: ['templates'],
    queryFn: templateApi.list,
  });

  const { data: testMessages = [] } = useQuery({
    queryKey: ['test-messages'],
    queryFn: templateApi.listTestMessages,
  });

  function selectTemplate(t: TemplateRow) {
    setSelected(t);
    setContent(t.template_content);
    setResourceType(t.resource_type ?? '');
    setResult(null);
  }

  // Auto-select test message when a template is selected or test messages arrive
  useEffect(() => {
    if (!selected || testMessages.length === 0) return;
    const match = testMessages.find(m => m.name === selected.template_name);
    if (match) setTestMessageName(match.name);
  }, [selected, testMessages]);

  async function handleTest() {
    if (!testMessageName) return;
    setIsTesting(true);
    setResult(null);
    try {
      const r = await templateApi.test({
        template_content: content,
        resource_type: resourceType,
        test_message_name: testMessageName,
      });
      setResult(r);
    } finally {
      setIsTesting(false);
    }
  }

  async function handleSave() {
    if (!selected) return;
    setIsSaving(true);
    setResult(null);
    try {
      const r = await templateApi.save(selected.template_name, {
        template_content: content,
        resource_type: resourceType,
      });
      setResult(r);
      if (r.valid) void refetch();
    } catch (err: unknown) {
      // The api.ts interceptor transforms axios errors into { status, message, data }
      // so we access err.data (not err.response.data)
      const e = err as Record<string, unknown>;
      const data = e?.data as (ValidationResult & { valid: boolean }) | undefined;
      if (data && data.valid === false) {
        setResult(data);
      } else {
        const message = typeof e?.message === 'string' ? e.message : 'Save failed';
        setResult({
          valid: false,
          errors: [message],
          warnings: [],
          fhir_output: '',
          test_message_used: 'none',
        });
      }
    } finally {
      setIsSaving(false);
    }
  }

  const fhirOutputFormatted = (() => {
    if (!result?.fhir_output) return '';
    try { return JSON.stringify(JSON.parse(result.fhir_output), null, 2); }
    catch { return result.fhir_output; }
  })();

  return (
    <div className="flex h-full gap-4 p-4">
      {/* Left: template list */}
      <aside className="w-56 shrink-0 flex flex-col gap-1 overflow-y-auto">
        <h2 className="text-sm font-semibold text-gray-500 uppercase mb-2">Templates</h2>
        {templates.map(t => (
          <button
            key={t.id}
            onClick={() => selectTemplate(t)}
            className={`text-left px-3 py-2 rounded text-sm truncate ${
              selected?.id === t.id
                ? 'bg-blue-100 text-blue-800 font-medium'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <span className="font-mono">{t.template_name}</span>
            {t.resource_type && (
              <span className="ml-1 text-xs text-gray-400">({t.resource_type})</span>
            )}
            <span className="ml-1 text-xs text-gray-400">v{t.version}</span>
          </button>
        ))}
      </aside>

      {/* Center: Monaco editor */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <input
            value={resourceType}
            onChange={e => setResourceType(e.target.value)}
            placeholder="resource_type (e.g. Patient)"
            className="border rounded px-2 py-1 text-sm w-44"
          />
          <select
            value={testMessageName}
            onChange={e => setTestMessageName(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">-- test message --</option>
            {testMessages.map(m => (
              <option key={m.id} value={m.name}>{m.name} ({m.message_type})</option>
            ))}
          </select>
          <button
            onClick={() => void handleTest()}
            disabled={isTesting || !testMessageName}
            className="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {isTesting ? 'Testing...' : '▶ Tester'}
          </button>
          <button
            onClick={() => void handleSave()}
            disabled={isSaving || !selected}
            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : '💾 Sauvegarder'}
          </button>
        </div>
        <div className="flex-1 border rounded overflow-hidden">
          <Editor
            height="100%"
            defaultLanguage="json"
            value={content}
            onChange={v => setContent(v ?? '')}
            theme="vs-dark"
            options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on' }}
          />
        </div>
      </div>

      {/* Right: result panel */}
      <aside className="w-96 shrink-0 flex flex-col gap-3 overflow-y-auto">
        {result && (
          <>
            <div className={`rounded px-3 py-2 text-sm font-medium ${
              result.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {result.valid ? 'Valide' : 'Invalide'}
              {result.version != null && ` — version ${result.version}`}
              {result.test_message_used !== 'none' && (
                <span className="ml-1 text-xs opacity-70">
                  (test: {result.test_message_used})
                </span>
              )}
            </div>

            {result.errors.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-red-700 mb-1">Erreurs</p>
                {result.errors.map((e, i) => (
                  <p key={i} className="text-xs text-red-600 bg-red-50 rounded px-2 py-1 mb-1">{e}</p>
                ))}
              </div>
            )}

            {result.warnings.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-yellow-700 mb-1">Avertissements</p>
                {result.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-yellow-700 bg-yellow-50 rounded px-2 py-1 mb-1">{w}</p>
                ))}
              </div>
            )}

            {fhirOutputFormatted && (
              <div className="flex-1">
                <p className="text-xs font-semibold text-gray-600 mb-1">Sortie FHIR</p>
                <pre className="text-xs bg-gray-900 text-green-300 rounded p-2 overflow-auto max-h-96">
                  {fhirOutputFormatted}
                </pre>
              </div>
            )}
          </>
        )}
        {!result && (
          <p className="text-sm text-gray-400 mt-8 text-center">
            Selectionnez un template et cliquez sur Tester
          </p>
        )}
      </aside>
    </div>
  );
}
